/**
 * Formula processing utilities for TableBuilder
 * Supports Excel-like formula syntax with SUM function and general math expressions
 */

import { evaluate } from 'mathjs';

/**
 * Check if a value is a formula (starts with =)
 */
export function isFormula(value) {
  return typeof value === 'string' && value.trim().startsWith('=');
}

/**
 * Convert column index to letter (0 -> A, 1 -> B, etc.)
 */
export function columnIndexToLetter(index) {
  let letter = '';
  let num = index;
  while (num >= 0) {
    letter = String.fromCharCode((num % 26) + 65) + letter;
    num = Math.floor(num / 26) - 1;
  }
  return letter;
}

/**
 * Convert column letter to index (A -> 0, B -> 1, etc.)
 */
export function columnLetterToIndex(letter) {
  let index = 0;
  for (let i = 0; i < letter.length; i++) {
    index = index * 26 + (letter.charCodeAt(i) - 65 + 1);
  }
  return index - 1;
}

/**
 * Convert cell reference to indices
 * @param {string} ref - Cell reference like "A1" or "B10"
 * @returns {{rowIndex: number, columnIndex: number} | null}
 */
export function cellRefToIndices(ref) {
  const match = ref
    .trim()
    .toUpperCase()
    .match(/^([A-Z]+)(\d+)$/);
  if (!match) return null;

  const [, columnLetter, rowNumber] = match;
  return {
    rowIndex: parseInt(rowNumber, 10) - 1,
    columnIndex: columnLetterToIndex(columnLetter),
  };
}

/**
 * Convert indices to cell reference
 * @param {number} rowIndex
 * @param {number} columnIndex
 * @returns {string} - Cell reference like "A1"
 */
export function indicesToCellRef(rowIndex, columnIndex) {
  return `${columnIndexToLetter(columnIndex)}${rowIndex + 1}`;
}

/**
 * Parse a range into array of cell references
 * @param {string} range - Range like "A1:A5" or "A1:B3"
 * @returns {Array<{rowIndex: number, columnIndex: number}>}
 */
export function parseRange(range) {
  const parts = range.split(':');
  if (parts.length !== 2) return [];

  const start = cellRefToIndices(parts[0]);
  const end = cellRefToIndices(parts[1]);

  if (!start || !end) return [];

  const cells = [];
  const minRow = Math.min(start.rowIndex, end.rowIndex);
  const maxRow = Math.max(start.rowIndex, end.rowIndex);
  const minCol = Math.min(start.columnIndex, end.columnIndex);
  const maxCol = Math.max(start.columnIndex, end.columnIndex);

  for (let row = minRow; row <= maxRow; row++) {
    for (let col = minCol; col <= maxCol; col++) {
      cells.push({ rowIndex: row, columnIndex: col });
    }
  }

  return cells;
}

/**
 * Parse formula into function name and arguments
 * @param {string} formula - Formula like "=SUM(A1:A5)" or "=SUM(A1,B2,C3)"
 * @returns {{function: string, args: string[]} | null}
 */
export function parseFormula(formula) {
  if (!isFormula(formula)) return null;

  // Remove the = sign
  const formulaBody = formula.slice(1).trim();

  // Match function name and arguments
  const match = formulaBody.match(/^([A-Z]+)\((.*)\)$/i);
  if (!match) return null;

  const [, functionName, argsString] = match;

  // Split arguments by comma (but respect nested parentheses if we add more complex formulas later)
  const args = argsString
    .split(',')
    .map((arg) => arg.trim())
    .filter((arg) => arg.length > 0);

  return {
    function: functionName.toUpperCase(),
    args,
  };
}

/**
 * Get all cell references used in a formula (including function-based and expression-based)
 * @param {string} formula - Formula string
 * @returns {Array<{rowIndex: number, columnIndex: number}>}
 */
export function getFormulaReferences(formula) {
  if (!isFormula(formula)) return [];

  const references = [];
  const formulaBody = formula.slice(1).trim();

  // First check if it's a function-based formula (like SUM)
  const parsed = parseFormula(formula);
  if (parsed) {
    // Function-based formula - extract references from arguments
    for (const arg of parsed.args) {
      if (arg.includes(':')) {
        // Range reference
        references.push(...parseRange(arg));
      } else {
        // Single cell reference
        const ref = cellRefToIndices(arg);
        if (ref) {
          references.push(ref);
        }
      }
    }
  } else {
    // Expression-based formula - find all cell references using regex
    // Match cell references like A1, B2, AA10, etc.
    const cellRefRegex = /\b([A-Z]+)(\d+)\b/g;
    let match;
    while ((match = cellRefRegex.exec(formulaBody)) !== null) {
      const ref = cellRefToIndices(match[0]);
      if (ref) {
        references.push(ref);
      }
    }
  }

  return references;
}

/**
 * Get cell value from table data
 * @param {number} rowIndex
 * @param {number} columnIndex
 * @param {Array} tableData
 * @returns {string | null}
 */
function getCellValue(rowIndex, columnIndex, tableData) {
  const cell = tableData.find((c) => c.rowIndex === rowIndex && c.columnIndex === columnIndex);
  return cell ? cell.value : null;
}

/**
 * Extract numeric values from function arguments (supports ranges and individual cells)
 * @param {Array<string>} args - Function arguments
 * @param {Array} tableData - Table data
 * @param {boolean} includeEmpty - Whether to include empty cells (default: false, treated as 0)
 * @returns {{values: Array<number>, hasError: boolean}}
 */
function extractNumericValues(args, tableData, includeEmpty = false) {
  const values = [];
  let hasError = false;

  for (const arg of args) {
    if (arg.includes(':')) {
      // Range reference
      const cells = parseRange(arg);
      for (const { rowIndex, columnIndex } of cells) {
        const value = getCellValue(rowIndex, columnIndex, tableData);
        if (value === null || value === '') {
          if (includeEmpty) {
            values.push(0);
          }
          continue;
        }
        const num = parseFloat(value);
        if (isNaN(num)) {
          hasError = true;
          break;
        }
        values.push(num);
      }
    } else {
      // Single cell reference or literal number
      const ref = cellRefToIndices(arg);
      if (ref) {
        // Cell reference
        const value = getCellValue(ref.rowIndex, ref.columnIndex, tableData);
        if (value === null || value === '') {
          if (includeEmpty) {
            values.push(0);
          }
          continue;
        }
        const num = parseFloat(value);
        if (isNaN(num)) {
          hasError = true;
          break;
        }
        values.push(num);
      } else {
        // Literal number
        const num = parseFloat(arg);
        if (isNaN(num)) {
          hasError = true;
          break;
        }
        values.push(num);
      }
    }

    if (hasError) break;
  }

  return { values, hasError };
}

/**
 * Evaluate SUM function
 * @param {Array<string>} args - Function arguments (cell refs or ranges)
 * @param {Array} tableData - Table data
 * @returns {string} - Result or error
 */
function evaluateSUM(args, tableData) {
  const { values, hasError } = extractNumericValues(args, tableData);

  if (hasError) {
    return '#NAN';
  }

  const sum = values.reduce((acc, val) => acc + val, 0);
  return sum.toString();
}

/**
 * Evaluate AVERAGE function
 * @param {Array<string>} args - Function arguments
 * @param {Array} tableData - Table data
 * @returns {string} - Result or error
 */
function evaluateAVERAGE(args, tableData) {
  const { values, hasError } = extractNumericValues(args, tableData);

  if (hasError) {
    return '#NAN';
  }

  if (values.length === 0) {
    return '#NAN';
  }

  const sum = values.reduce((acc, val) => acc + val, 0);
  const average = sum / values.length;
  return average.toString();
}

/**
 * Evaluate COUNT function (counts numeric values)
 * @param {Array<string>} args - Function arguments
 * @param {Array} tableData - Table data
 * @returns {string} - Result or error
 */
function evaluateCOUNT(args, tableData) {
  const { values } = extractNumericValues(args, tableData);
  return values.length.toString();
}

/**
 * Evaluate COUNTA function (counts non-empty cells)
 * @param {Array<string>} args - Function arguments
 * @param {Array} tableData - Table data
 * @returns {string} - Result or error
 */
function evaluateCOUNTA(args, tableData) {
  let count = 0;

  for (const arg of args) {
    if (arg.includes(':')) {
      // Range reference
      const cells = parseRange(arg);
      for (const { rowIndex, columnIndex } of cells) {
        const value = getCellValue(rowIndex, columnIndex, tableData);
        if (value !== null && value !== '') {
          count++;
        }
      }
    } else {
      // Single cell reference
      const ref = cellRefToIndices(arg);
      if (ref) {
        const value = getCellValue(ref.rowIndex, ref.columnIndex, tableData);
        if (value !== null && value !== '') {
          count++;
        }
      }
    }
  }

  return count.toString();
}

/**
 * Evaluate MIN function
 * @param {Array<string>} args - Function arguments
 * @param {Array} tableData - Table data
 * @returns {string} - Result or error
 */
function evaluateMIN(args, tableData) {
  const { values, hasError } = extractNumericValues(args, tableData);

  if (hasError) {
    return '#NAN';
  }

  if (values.length === 0) {
    return '#NAN';
  }

  const min = Math.min(...values);
  return min.toString();
}

/**
 * Evaluate MAX function
 * @param {Array<string>} args - Function arguments
 * @param {Array} tableData - Table data
 * @returns {string} - Result or error
 */
function evaluateMAX(args, tableData) {
  const { values, hasError } = extractNumericValues(args, tableData);

  if (hasError) {
    return '#NAN';
  }

  if (values.length === 0) {
    return '#NAN';
  }

  const max = Math.max(...values);
  return max.toString();
}

/**
 * Evaluate ROUND function
 * @param {Array<string>} args - Function arguments [value, decimals]
 * @param {Array} tableData - Table data
 * @returns {string} - Result or error
 */
function evaluateROUND(args, tableData) {
  if (args.length < 1 || args.length > 2) {
    return '#ERROR';
  }

  const { values, hasError } = extractNumericValues([args[0]], tableData);

  if (hasError || values.length === 0) {
    return '#NAN';
  }

  const value = values[0];
  const decimals = args.length > 1 ? parseInt(args[1]) : 0;

  if (isNaN(decimals)) {
    return '#ERROR';
  }

  const multiplier = Math.pow(10, decimals);
  const rounded = Math.round(value * multiplier) / multiplier;
  return rounded.toString();
}

/**
 * Evaluate ABS function
 * @param {Array<string>} args - Function arguments [value]
 * @param {Array} tableData - Table data
 * @returns {string} - Result or error
 */
function evaluateABS(args, tableData) {
  if (args.length !== 1) {
    return '#ERROR';
  }

  const { values, hasError } = extractNumericValues(args, tableData);

  if (hasError || values.length === 0) {
    return '#NAN';
  }

  const absolute = Math.abs(values[0]);
  return absolute.toString();
}

/**
 * Evaluate SQRT function
 * @param {Array<string>} args - Function arguments [value]
 * @param {Array} tableData - Table data
 * @returns {string} - Result or error
 */
function evaluateSQRT(args, tableData) {
  if (args.length !== 1) {
    return '#ERROR';
  }

  const { values, hasError } = extractNumericValues(args, tableData);

  if (hasError || values.length === 0) {
    return '#NAN';
  }

  if (values[0] < 0) {
    return '#NAN';
  }

  const sqrt = Math.sqrt(values[0]);
  return sqrt.toString();
}

/**
 * Evaluate POWER function
 * @param {Array<string>} args - Function arguments [base, exponent]
 * @param {Array} tableData - Table data
 * @returns {string} - Result or error
 */
function evaluatePOWER(args, tableData) {
  if (args.length !== 2) {
    return '#ERROR';
  }

  const { values, hasError } = extractNumericValues(args, tableData);

  if (hasError || values.length !== 2) {
    return '#NAN';
  }

  const result = Math.pow(values[0], values[1]);
  return result.toString();
}

/**
 * Evaluate IF function
 * @param {Array<string>} args - Function arguments [condition, trueValue, falseValue]
 * @param {Array} tableData - Table data
 * @returns {string} - Result or error
 */
function evaluateIF(args, tableData) {
  if (args.length !== 3) {
    return '#ERROR';
  }

  try {
    // Evaluate the condition using math.js
    const { expression: conditionExpr, hasError } = replaceCellReferences(args[0], tableData);

    if (hasError) {
      return '#NAN';
    }

    const conditionResult = evaluate(conditionExpr);

    // Determine which branch to take
    const selectedArg = conditionResult ? args[1] : args[2];

    // Try to parse as cell reference first
    const ref = cellRefToIndices(selectedArg);
    if (ref) {
      const value = getCellValue(ref.rowIndex, ref.columnIndex, tableData);
      return value !== null && value !== '' ? value : '';
    }

    // Try to parse as number
    const num = parseFloat(selectedArg);
    if (!isNaN(num)) {
      return num.toString();
    }

    // Return as string (remove quotes if present)
    return selectedArg.replace(/^["']|["']$/g, '');
  } catch (error) {
    console.error('IF function evaluation error:', error);
    return '#ERROR';
  }
}

/**
 * Detect circular references in formulas
 * @param {number} rowIndex - Starting cell row
 * @param {number} columnIndex - Starting cell column
 * @param {Array} tableData - Table data
 * @param {Set} visited - Set of visited cells (for recursion)
 * @returns {boolean} - True if circular reference detected
 */
export function hasCircularReference(rowIndex, columnIndex, tableData, visited = new Set()) {
  const cellKey = `${rowIndex}-${columnIndex}`;

  // If we've already visited this cell in the current path, we have a circular reference
  if (visited.has(cellKey)) {
    return true;
  }

  // Find the cell
  const cell = tableData.find((c) => c.rowIndex === rowIndex && c.columnIndex === columnIndex);

  // If cell doesn't have a formula, no circular reference from this path
  if (!cell || !cell.formula || !isFormula(cell.formula)) {
    return false;
  }

  // Add current cell to visited set
  visited.add(cellKey);

  // Get all references in this cell's formula
  const references = getFormulaReferences(cell.formula);

  // Check each referenced cell for circular references
  for (const ref of references) {
    if (hasCircularReference(ref.rowIndex, ref.columnIndex, tableData, new Set(visited))) {
      return true;
    }
  }

  return false;
}

/**
 * Replace cell references in an expression with their values
 * @param {string} expression - Math expression with cell references
 * @param {Array} tableData - Table data
 * @returns {{expression: string, hasError: boolean}} - Expression with values substituted
 */
function replaceCellReferences(expression, tableData) {
  let hasError = false;
  const cellRefRegex = /\b([A-Z]+)(\d+)\b/g;

  const replaced = expression.replace(cellRefRegex, (match) => {
    const ref = cellRefToIndices(match);
    if (!ref) {
      hasError = true;
      return '0';
    }

    const value = getCellValue(ref.rowIndex, ref.columnIndex, tableData);

    // Empty cells are treated as 0
    if (value === null || value === '') {
      return '0';
    }

    // Check if it's a number
    const num = parseFloat(value);
    if (isNaN(num)) {
      hasError = true;
      return '0';
    }

    return num.toString();
  });

  return { expression: replaced, hasError };
}

/**
 * Evaluate a math expression formula
 * @param {string} expression - Math expression like "A1 + B2 * 3"
 * @param {Array} tableData - Table data
 * @returns {string} - Computed result or error
 */
function evaluateMathExpression(expression, tableData) {
  // Replace cell references with their values
  const { expression: replacedExpression, hasError } = replaceCellReferences(expression, tableData);

  if (hasError) {
    return '#NAN';
  }

  try {
    const result = evaluate(replacedExpression);

    // Check if result is a number
    if (typeof result === 'number') {
      // Round to avoid floating point precision issues
      return Number.isInteger(result) ? result.toString() : result.toFixed(10).replace(/\.?0+$/, '');
    }

    return result.toString();
  } catch (error) {
    console.error('Math expression evaluation error:', error);
    return '#ERROR';
  }
}

/**
 * Evaluate a formula
 * @param {string} formula - Formula string like "=SUM(A1:A5)" or "=A1 + B2 * 3"
 * @param {Array} tableData - Current table data
 * @param {number} currentRow - Row index of cell being evaluated
 * @param {number} currentCol - Column index of cell being evaluated
 * @returns {string} - Computed result or error
 */
export function evaluateFormula(formula, tableData, currentRow, currentCol) {
  if (!isFormula(formula)) {
    return formula;
  }

  // Check for circular references
  if (hasCircularReference(currentRow, currentCol, tableData)) {
    return '#CIRCULAR';
  }

  const formulaBody = formula.slice(1).trim();

  // Check if it's a function-based formula
  const parsed = parseFormula(formula);

  if (parsed) {
    // Function-based formula (like SUM, AVERAGE, etc.)
    switch (parsed.function) {
      case 'SUM':
        return evaluateSUM(parsed.args, tableData);
      case 'AVERAGE':
      case 'AVG':
        return evaluateAVERAGE(parsed.args, tableData);
      case 'COUNT':
        return evaluateCOUNT(parsed.args, tableData);
      case 'COUNTA':
        return evaluateCOUNTA(parsed.args, tableData);
      case 'MIN':
        return evaluateMIN(parsed.args, tableData);
      case 'MAX':
        return evaluateMAX(parsed.args, tableData);
      case 'ROUND':
        return evaluateROUND(parsed.args, tableData);
      case 'ABS':
        return evaluateABS(parsed.args, tableData);
      case 'SQRT':
        return evaluateSQRT(parsed.args, tableData);
      case 'POWER':
      case 'POW':
        return evaluatePOWER(parsed.args, tableData);
      case 'IF':
        return evaluateIF(parsed.args, tableData);
      default:
        return '#ERROR';
    }
  } else {
    // Expression-based formula (like A1 + B2 * 3)
    return evaluateMathExpression(formulaBody, tableData);
  }
}

/**
 * Recalculate all formulas in the table
 * @param {Array} tableData - Table data
 * @returns {Array} - Updated table data with recalculated values
 */
export function recalculateFormulas(tableData) {
  return tableData.map((cell) => {
    if (cell.formula && isFormula(cell.formula)) {
      return {
        ...cell,
        value: evaluateFormula(cell.formula, tableData, cell.rowIndex, cell.columnIndex),
      };
    }
    return cell;
  });
}
